<template>
  <v-app>
    <a href="#main-content" class="skip-link">
      {{ $t('common.skipToContent') }}
    </a>
    <app-bar
      @toggle-drawer="showDrawer = !showDrawer"
      @open-search="searchDialog = true"
      @open-shortcuts="shortcutDialog = true"
    >
      <template #walletConnect>
        <div v-if="!isLoggined && !hasInjectedWallet">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                class="bright--text"
                icon
                depressed
                :aria-label="$t('appbar.walletConnect')"
                @click="wallect_connect"
                v-bind="attrs"
                v-on="on"
              >
                <img
                  src="@/assets/wallet-connect-logo.svg"
                  alt="Icon"
                  width="32"
                  height="32"
                />
              </v-btn>
            </template>
            <span>{{ $t('appbar.walletConnect') }}</span>
          </v-tooltip>
        </div>
      </template>
      <template #loginButton>
        <div v-if="isLoggined">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                class="bright--text mr-3"
                icon
                depressed
                :aria-label="$t('appbar.logout')"
                @click="logout"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-logout</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('appbar.logout') }}</span>
          </v-tooltip>
        </div>
        <div v-else>
          <v-menu offset-y>
            <template #activator="{ on: menuOn, attrs: menuAttrs }">
              <v-tooltip bottom>
                <template #activator="{ on: tipOn, attrs: tipAttrs }">
                  <v-btn
                    class="bright--text mr-3"
                    icon
                    depressed
                    :aria-label="$t('appbar.login')"
                    v-bind="{ ...menuAttrs, ...tipAttrs }"
                    v-on="{ ...menuOn, ...tipOn }"
                  >
                    <v-icon>mdi-login</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('appbar.login') }}</span>
              </v-tooltip>
            </template>
            <v-list dense>
              <v-list-item @click="login">
                <v-list-item-icon class="mr-2">
                  <v-icon>mdi-wallet</v-icon>
                </v-list-item-icon>
                <v-list-item-title>MetaMask</v-list-item-title>
              </v-list-item>
              <v-list-item @click="googleLogin">
                <v-list-item-icon class="mr-2">
                  <v-icon>mdi-google</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Google</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
    </app-bar>

    <nav-menu
      @drawerWatch="updateChildDrawer"
      :value="showDrawer"
      :menulist="menulist"
    />

    <v-main v-if="isLoggined" id="main-content">
      <error-banner />
      <div
        :class="
          $vuetify.breakpoint.xsOnly
            ? 'pa-3'
            : $vuetify.breakpoint.smOnly
            ? 'pa-4'
            : 'pa-5'
        "
      >
        <router-view />
      </div>
    </v-main>

    <v-snackbar
      v-model="$store.state.toast.show"
      :color="$store.state.toast.color"
      :timeout="$store.state.toast.timeout"
      top
      right
    >
      {{ $store.state.toast.message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="$store.commit('toast/HIDE_TOAST')">
          {{ $t('common.close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="loginDialog" hide-overlay persistent width="320">
      <v-card color="primary" dark>
        <v-card-text class="pa-5">
          <div class="text-center mb-3">
            <v-icon size="40" color="white">mdi-wallet-outline</v-icon>
          </div>
          <div class="text-center white--text mb-2">
            {{ $t('app.connecting') }}
          </div>
          <div class="text-center white--text text-body-2 mb-3">
            {{ $t('app.signMessage') }}
          </div>
          <v-progress-linear
            indeterminate
            color="white"
            class="mt-3"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <global-search
      v-model="searchDialog"
      :menu-items="searchableMenuItems"
      @select="onSearchSelect"
    />

    <shortcut-help-dialog v-model="shortcutDialog" />

    <!-- 2FA Verification Dialog -->
    <v-dialog v-model="twoFADialog" persistent max-width="400">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon class="mr-2" color="primary">mdi-shield-lock</v-icon>
          {{ $t('twofa.loginTitle') }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-4">{{ $t('twofa.loginDesc') }}</p>
          <v-text-field
            v-model="twoFACode"
            :label="$t('twofa.enterCode')"
            outlined
            dense
            maxlength="6"
            autofocus
            :error-messages="twoFAError"
            @keyup.enter="submitTwoFA"
          />
          <!-- WebAuthn security key option -->
          <template v-if="hasWebAuthn">
            <v-divider class="my-3" />
            <div class="text-center text-body-2 mb-2">
              {{ $t('webauthn.or') }}
            </div>
            <v-btn
              block
              outlined
              color="primary"
              :loading="webAuthnLoading"
              @click="submitWebAuthn"
            >
              <v-icon left>mdi-key-variant</v-icon>
              {{ $t('webauthn.loginButton') }}
            </v-btn>
            <p
              v-if="webAuthnLoading"
              class="text-caption text-center mt-2 grey--text"
            >
              {{ $t('webauthn.loginWaiting') }}
            </p>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelTwoFA">{{ $t('common.cancel') }}</v-btn>
          <v-btn
            color="primary"
            :loading="twoFALoading"
            :disabled="twoFACode.length !== 6"
            @click="submitTwoFA"
          >
            {{ $t('twofa.verify') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import AppBar from '@/components/Application/AppBar.vue'
import NavMenu from '@/components/Application/NavMenu.vue'
import ErrorBanner from '@/components/common/ErrorBanner.vue'
import GlobalSearch from '@/components/common/GlobalSearch.vue'
import ShortcutHelpDialog from '@/components/common/ShortcutHelpDialog.vue'
import { MENU_ITEMS, MENU_ROUTE_SETS } from '@/constants/menuItems'
import { isWebAuthnSupported } from '@/services/webauthnService'

import { Buffer } from 'buffer'
import { getMenuList } from '@/services/settingsService'

// @ts-ignore
window.Buffer = Buffer

import { configureChains, createConfig } from '@wagmi/core'
import { bsc, mainnet } from '@wagmi/core/chains'
import { EthereumClient, w3mConnectors, w3mProvider } from '@web3modal/ethereum'
import { Web3Modal } from '@web3modal/html'
import { getAccount, signMessage, disconnect } from '@wagmi/core'

const projectId = process.env.VUE_APP_WEB3MODAL_PROJECT_ID

export default {
  name: 'App',

  components: {
    AppBar,
    NavMenu,
    ErrorBanner,
    GlobalSearch,
    ShortcutHelpDialog,
  },

  data: () => ({
    showDrawer: true,
    chainId: '',
    loginDialog: false,
    searchDialog: false,
    shortcutDialog: false,
    pendingGo: false,
    jwt: null,
    menulist: [],
    notificationPollingInterval: null,
    // 2FA dialog
    twoFADialog: false,
    twoFACode: '',
    twoFAError: '',
    twoFALoading: false,
    // WebAuthn
    hasWebAuthn: false,
    webAuthnLoading: false,
  }),
  watch: {
    showDrawer(/* oldValue, newValue */) {},
    twoFACode(val) {
      // Clear error when user starts typing a new code
      if (this.twoFAError) {
        this.twoFAError = ''
      }
      if (val && val.length === 6 && !this.twoFALoading) {
        this.submitTwoFA()
      }
    },
  },
  computed: {
    hasInjectedWallet() {
      return !!window.ethereum
    },
    loading() {
      return this.$store.getters.loading
    },
    isLoggined() {
      return this.$store.getters['auth/isLoggined']
    },
    address() {
      return this.$store.getters['auth/address']
    },
    isAdmin() {
      return this.$store.getters['auth/isAdmin']
    },
    searchableMenuItems() {
      const menulistSet = new Set(this.menulist)
      const categoryKeys = {
        settings: 'nav.settings',
        adminSettings: 'nav.settings',
        erc20: 'nav.erc20',
        erc721: 'nav.erc721',
        erc1155: 'nav.erc1155',
        board: 'nav.board',
        arena: 'nav.arena',
        logData: 'nav.logData',
        metaqData: 'nav.metaq',
      }
      const getCategoryForRoute = (route) => {
        for (const [key, routeSet] of Object.entries(MENU_ROUTE_SETS)) {
          if (routeSet.has(route)) {
            return categoryKeys[key] ? this.$t(categoryKeys[key]) : key
          }
        }
        return ''
      }

      return MENU_ITEMS.filter((item) => {
        // Admin 전용 메뉴
        if (MENU_ROUTE_SETS.adminSettings.has(item.route)) {
          return this.isAdmin
        }
        // Log Data / METAQ 메뉴 (metaplanet_token_log 권한 필요)
        if (
          MENU_ROUTE_SETS.logData.has(item.route) ||
          MENU_ROUTE_SETS.metaqData.has(item.route)
        ) {
          return menulistSet.has('/metaplanet_token_log')
        }
        // 일반 메뉴 — menulist 기반 권한 체크
        return menulistSet.has(item.route)
      }).map((item) => ({
        text: this.$t(item.text),
        route: item.route,
        icon: item.icon,
        category: getCategoryForRoute(item.route),
      }))
    },
  },
  methods: {
    onSearchSelect(route) {
      this.searchDialog = false
      this.$router.push(route).catch(() => {})
    },
    handleGlobalKeydown(e) {
      // Ctrl+K: Global search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        this.searchDialog = !this.searchDialog
        return
      }

      // Ctrl+/: Shortcut help
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault()
        this.shortcutDialog = !this.shortcutDialog
        return
      }

      // Skip shortcuts when typing in input fields
      const tag = e.target?.tagName?.toLowerCase()
      const isEditable =
        tag === 'input' || tag === 'textarea' || e.target?.isContentEditable
      if (isEditable) return

      // Skip if modifier keys held (except shift for ?)
      if (e.ctrlKey || e.metaKey || e.altKey) return

      // ? (Shift+/): Shortcut help
      if (e.key === '?') {
        e.preventDefault()
        this.shortcutDialog = !this.shortcutDialog
        return
      }

      // G prefix for navigation shortcuts
      if (this.pendingGo) {
        this.pendingGo = false
        const routes = {
          h: '/dashboard',
          s: '/preferences',
          m: '/lbank_monitor',
        }
        const route = routes[e.key.toLowerCase()]
        if (route && this.$route.path !== route) {
          e.preventDefault()
          this.$router.push(route).catch(() => {})
        }
        return
      }

      if (e.key.toLowerCase() === 'g') {
        this.pendingGo = true
        setTimeout(() => {
          this.pendingGo = false
        }, 1000)
        return
      }

      // R: Refresh table
      if (e.key.toLowerCase() === 'r') {
        e.preventDefault()
        this.$root.$emit('shortcut:refresh')
        return
      }
    },
    updateChildDrawer(value) {
      this.showDrawer = value
    },
    startNotificationPolling() {
      this.$store.dispatch('notification/fetchUnreadCount')
      this.notificationPollingInterval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          this.$store.dispatch('notification/fetchUnreadCount')
        }
      }, 30000)
    },
    stopNotificationPolling() {
      if (this.notificationPollingInterval) {
        clearInterval(this.notificationPollingInterval)
        this.notificationPollingInterval = null
      }
      this.$store.commit('notification/CLEAR')
    },
    async logginCallback(status) {
      if (status === '2fa_required') {
        this.twoFADialog = true
        this.twoFACode = ''
        this.twoFAError = ''
        // Show WebAuthn option if the browser supports it
        this.hasWebAuthn = isWebAuthnSupported()
        return
      }
      if (status) {
        const { data } = await getMenuList(null, { silent: true })
        if (data) {
          this.menulist = (data.list || []).map((item) => item.route)
        }

        this.$store.dispatch('auth/checkLoginStatus')
        this.startNotificationPolling()
      }
    },
    async submitTwoFA() {
      this.twoFALoading = true
      this.twoFAError = ''
      this.$store.dispatch('auth/verifyTwoFA', {
        totpCode: this.twoFACode,
        callback: async (success) => {
          this.twoFALoading = false
          if (success) {
            this.twoFADialog = false
            this.twoFACode = ''
            // Complete login flow
            const { data } = await getMenuList(null, { silent: true })
            if (data) {
              this.menulist = (data.list || []).map((item) => item.route)
            }
            this.$store.dispatch('auth/checkLoginStatus')
            this.startNotificationPolling()
          } else {
            this.twoFAError = this.$t('twofa.invalidCode')
            this.twoFACode = ''
          }
        },
      })
    },
    async submitWebAuthn() {
      this.webAuthnLoading = true
      this.$store.dispatch('auth/verifyWebAuthn', {
        callback: async (success) => {
          this.webAuthnLoading = false
          if (success) {
            this.twoFADialog = false
            this.twoFACode = ''
            // Complete login flow
            const { data } = await getMenuList(null, { silent: true })
            if (data) {
              this.menulist = (data.list || []).map((item) => item.route)
            }
            this.$store.dispatch('auth/checkLoginStatus')
            this.startNotificationPolling()
          }
        },
      })
    },
    cancelTwoFA() {
      this.twoFADialog = false
      this.twoFACode = ''
      this.twoFAError = ''
      this.webAuthnLoading = false
      this.$store.commit('auth/clearTwoFA')
    },
    loadGsiScript() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.accounts) {
          resolve()
          return
        }
        const script = document.createElement('script')
        script.src = 'https://accounts.google.com/gsi/client'
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
      })
    },
    async googleLogin() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID
      if (!clientId) {
        this.$store.dispatch('toast/error', this.$t('app.googleNotConfigured'))
        return
      }
      try {
        await this.loadGsiScript()
      } catch (_) {
        this.$store.dispatch('toast/error', this.$t('app.googleNotConfigured'))
        return
      }
      // eslint-disable-next-line no-undef
      google.accounts.id.initialize({
        client_id: clientId,
        callback: this.handleGoogleCallback,
        ux_mode: 'popup',
      })
      // Hidden button + programmatic click for reliable popup
      const container = document.createElement('div')
      container.style.display = 'none'
      document.body.appendChild(container)
      // eslint-disable-next-line no-undef
      google.accounts.id.renderButton(container, {
        type: 'standard',
        size: 'large',
      })
      const btn =
        container.querySelector('[role=button]') ||
        container.querySelector('div[id^=gsi]')
      if (btn) {
        btn.click()
      } else {
        // eslint-disable-next-line no-undef
        google.accounts.id.prompt()
      }
      setTimeout(() => container.remove(), 1000)
    },
    handleGoogleCallback(response) {
      this.$store.dispatch('auth/googleLogin', {
        credential: response.credential,
        callback: this.logginCallback,
      })
    },
    async login() {
      await window.ethereum.request({
        method: 'wallet_requestPermissions',
        params: [
          {
            eth_accounts: {},
          },
        ],
      })
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      })
      const address = accounts[0]

      // 보안 강화: nonce와 timestamp 추가 (Replay Attack 방지)
      const timestamp = Date.now()
      const nonce = Math.random().toString(36).substring(2, 15)
      const message = `Sign to authenticate with TMS.\n\nAddress: ${address}\nTimestamp: ${timestamp}\nNonce: ${nonce}`

      // Sign the message using MetaMask
      const signedMessage = await window.ethereum.request({
        method: 'personal_sign',
        params: [message, address],
      })

      this.$store.dispatch('auth/login', {
        address,
        message,
        signature: signedMessage,
        callback: this.logginCallback,
      })
    },
    async logout() {
      this.stopNotificationPolling()
      await this.$store.dispatch('auth/logout', {
        connector: this.connector,
      })

      // Only try to disconnect wagmi if it was used (wallet connect)
      // MetaMask doesn't use wagmi, so this may fail - that's OK
      try {
        await disconnect()
      } catch (_) {
        // Intentional: wagmi disconnect errors are expected when using MetaMask
      }
      window.location.reload()
    },
    async wallect_connect() {
      if (!window.ethereum) {
        this.$toast.error(
          this.$t
            ? this.$t('auth.noMetamask')
            : 'MetaMask is not installed. Please install MetaMask extension.'
        )
        return
      }
      // Clear only WalletConnect/wagmi related keys
      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith('wc@') ||
          key.startsWith('wagmi') ||
          key.startsWith('W3M') ||
          key.startsWith('@w3m')
        ) {
          localStorage.removeItem(key)
        }
      })

      // 1. Define constants
      const chains = [bsc, mainnet]

      // 2. Configure wagmi client
      const { publicClient } = configureChains(chains, [
        w3mProvider({ projectId }),
      ])
      const wagmiConfig = createConfig({
        autoConnect: true,
        connectors: w3mConnectors({ chains, version: 1, projectId }),
        publicClient,
      })

      // 3. Create ethereum and modal clients
      const ethereumClient = new EthereumClient(wagmiConfig, chains)
      const web3Modal = new Web3Modal(
        {
          projectId,
          walletConnectVersion: 2,
          themeMode: this.$vuetify.theme.dark ? 'dark' : 'light',
        },
        ethereumClient
      )

      if (getAccount().status != 'connected') {
        await web3Modal.openModal()
        let unsubscribe = undefined
        try {
          await new Promise((resolve, reject) => {
            unsubscribe = web3Modal.subscribeModal((newState) => {
              if (!newState.open) {
                resolve()
              } else {
                reject()
              }
            })
          })
          unsubscribe()
        } catch (error) {
          return
        }

        if (getAccount().connector == undefined) {
          await disconnect()
          return
        }
      }

      const address = getAccount().address

      // 보안 강화: nonce와 timestamp 추가 (Replay Attack 방지)
      const timestamp = Date.now()
      const nonce = Math.random().toString(36).substring(2, 15)
      const message = `Sign to authenticate with TMS.\n\nAddress: ${address}\nTimestamp: ${timestamp}\nNonce: ${nonce}`

      try {
        const signedMessage = await signMessage({ message })
        this.$store.dispatch('auth/login', {
          address,
          message,
          signature: signedMessage,
          callback: this.logginCallback,
        })
      } catch (error) {
        await disconnect()
        this.$toast.error(this.$t('app.connectionCancelled'))
      }
    },
  },
  async created() {
    this.$store.dispatch('preferences/loadPreferences')

    const vuetifyTheme = localStorage.getItem('vuetifyTheme')
      ? JSON.parse(localStorage.getItem('vuetifyTheme'))
      : null

    if (vuetifyTheme == null) {
      // OS 설정 감지, localStorage에 저장하지 않음 (시스템 추종 유지)
      const prefersDark = window.matchMedia?.(
        '(prefers-color-scheme: dark)'
      ).matches
      this.$vuetify.theme.dark = !!prefersDark
    } else {
      this.$vuetify.theme.dark = vuetifyTheme.dark
    }

    // 시스템 설정 변경 실시간 감지 (수동 토글 후에는 무시)
    if (window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      const handler = (e) => {
        if (!localStorage.getItem('vuetifyTheme')) {
          this.$vuetify.theme.dark = e.matches
        }
      }
      mq.addEventListener?.('change', handler) || mq.addListener?.(handler)
    }
  },
  async mounted() {
    document.addEventListener('keydown', this.handleGlobalKeydown)
    await this.$store.dispatch('auth/checkLoginStatus')
    if (this.isLoggined) {
      this.logginCallback(true)
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleGlobalKeydown)
    this.stopNotificationPolling()
  },
}
</script>

<style lang="scss">
/* Skip to Content Link */
.skip-link {
  position: absolute;
  top: -40px;
  left: 0;
  background: var(--v-primary-base, #37474f);
  color: white;
  padding: 8px 16px;
  z-index: 100;
  transition: top 0.2s;
  text-decoration: none;
}
.skip-link:focus {
  top: 0;
}

/* Transitions */
.bounce-transition {
  &-enter-active,
  &-leave,
  &-leave-to {
    transition: all 0.2s ease;
  }
  &-enter,
  &-leave-to {
    transform: translateY(100px);
    opacity: 0;
  }
}

/* Web3Modal */
w3m-modal {
  z-index: 1000;
}

/* Data Table */
.v-data-table__wrapper {
  overflow-x: auto !important;
}
.v-application .v-data-table {
  &.outlined {
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
  }
  table {
    table-layout: fixed;
    td,
    th {
      text-align: left !important;
      padding: 0 12px !important;
      word-break: break-word;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    th {
      font-weight: 600 !important;
      font-size: 12px !important;
      color: #546e7a !important;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      &.sortable .v-data-table-header__icon {
        position: absolute;
        color: #546e7a;
      }
    }
    td {
      text-align: left !important;
    }
    .v-input--checkbox .v-input__slot {
      justify-content: space-around;
      .v-input--selection-controls__input {
        margin: 0;
      }
    }
    .left {
      text-align: left !important;
    }
    .right {
      text-align: right !important;
    }
  }
}

.theme--dark .v-application .v-data-table {
  table th {
    color: #b0bec5 !important;
    &.sortable .v-data-table-header__icon {
      color: #b0bec5;
    }
  }
}

/* Input Fields */
.v-input {
  .theme--dark.v-label {
    color: #b0bec5 !important;
  }
}
.primary--text .v-input__control > .v-input__slot:after {
  border-color: rgba(84, 110, 122, 0.5) !important;
}
.v-application .primary {
  background: #37474f !important;
  color: #fff;
}

/* Cards */
.v-card {
  .col {
    padding: 0 12px !important;
    &:first-child {
      padding-top: 12px !important;
    }
  }
  .v-card__actions > .v-btn.v-btn {
    min-width: 85px;
    border-radius: 6px;
  }
}

/* Overlay */
.theme--dark .v-overlay__scrim {
  background-color: rgba(0, 0, 0, 0.6) !important;
}
.theme--light .v-overlay__scrim {
  background-color: rgba(0, 0, 0, 0.4) !important;
}
.theme--light.v-card > .v-card__text,
.theme--light.v-card > .v-card__subtitle {
  color: rgba(0, 0, 0, 0.87) !important;
}
.theme--dark.v-card > .v-card__text,
.theme--dark.v-card > .v-card__subtitle {
  color: rgba(255, 255, 255, 0.87) !important;
}

@media (max-width: 1263px) {
  h1 {
    font-size: 24px;
  }
}

/* Action Buttons */
.v-data-table td .v-btn.v-btn--has-bg.link-btn {
  background: none;
  box-shadow: unset;
}

/* Hover Area */
.hover_area {
  height: inherit;
  position: relative;
  line-height: 48px;
  max-width: 99%;
  .hover_address {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
  }
  .hover_inner {
    transition: all 0.2s ease;
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    line-height: 48px;
    text-align: center;
    button {
      transition: all 0.2s ease;
      box-shadow: none;
      min-width: auto !important;
      padding: 0 !important;
      width: 32px !important;
      height: 32px !important;
      margin: 0 2px;
      border-radius: 6px;
      i {
        font-size: 14px;
      }
      &.edit {
        border: 1px solid #43a047;
        background: #fff !important;
        color: #43a047;
        &:hover {
          background: #43a047 !important;
          color: #fff !important;
        }
      }
      &.copy {
        border: 1px solid #546e7a;
        background: #fff !important;
        color: #546e7a;
        &:hover {
          background: #546e7a !important;
          color: #fff !important;
        }
      }
      &.link {
        border: 1px solid #e53935;
        background: #fff !important;
        color: #e53935;
        &:hover {
          background: #e53935 !important;
          color: #fff !important;
        }
      }
    }
  }
  &:hover .hover_inner {
    opacity: 1;
  }
}
.theme--dark .hover_area .hover_inner button {
  &.edit {
    background: var(--v-surface-base) !important;
  }
  &.copy {
    background: var(--v-surface-base) !important;
  }
  &.link {
    background: var(--v-surface-base) !important;
  }
}

/* Link Button */
.link-btn {
  border: 1px solid #e53935;
  background: #fff !important;
  color: #e53935 !important;
  width: 32px !important;
  height: 32px !important;
  margin: 0;
  border-radius: 6px;
  min-width: auto !important;
  transition: all 0.2s ease;
}
.link-btn i {
  font-size: 14px !important;
}
.link-btn:hover {
  background: #e53935 !important;
  color: #fff !important;
}
.theme--dark .link-btn {
  background: var(--v-surface-base) !important;
}

/* Select */
.v-select__selections input[readonly] {
  line-height: 0;
  font-size: 0;
  padding: 0;
}

/* Selected Row */
.v-data-table tr.select-tr {
  background: rgba(55, 71, 79, 0.1) !important;
}
.theme--dark.v-data-table tr.select-tr {
  background: rgba(255, 255, 255, 0.08) !important;
}

/* Labels */
.v-input .theme--dark.v-label {
  color: #b0bec5 !important;
}

/* Search Row */
.search_row .v-text-field__details {
  display: none !important;
}

/* Button Row */
.btn-row {
  display: flex;
  gap: 8px;
}
.btn-row > button {
  margin: 8px 0;
  border-radius: 6px;
}

/* Menu Content */
.theme--light .v-menu__content {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
}
.theme--dark .v-menu__content {
  background: rgba(38, 38, 38, 0.95) !important;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}

/* Login Dialog */
.v-dialog {
  .v-card.primary {
    background: #37474f !important;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
  }
}
</style>
